type TapHandler = (e: { clientX: number; clientY: number }) => void;
type PinchHandler = (e: { factor: number }) => void;
type DragHandler = (e: { dx: number; dy: number; clientX: number; clientY: number }) => void;
type DragStartHandler = (e: { clientX: number; clientY: number }) => void;
type DragEndHandler = () => void;

const TAP_MAX_MOVE = 12;
const TAP_MAX_TIME = 250;

interface Tracked {
  startX: number;
  startY: number;
  startT: number;
  x: number;
  y: number;
  moved: boolean;
}

export class CanvasGestures {
  private pointers = new Map<number, Tracked>();
  private pinchStartDist = 0;
  private tapHandlers: TapHandler[] = [];
  private pinchHandlers: PinchHandler[] = [];
  private dragHandlers: DragHandler[] = [];
  private dragStartHandlers: DragStartHandler[] = [];
  private dragEndHandlers: DragEndHandler[] = [];
  private dragging = false;

  constructor(private el: HTMLElement) {
    el.addEventListener("pointerdown", this.onDown);
    el.addEventListener("pointermove", this.onMove);
    el.addEventListener("pointerup", this.onUp);
    el.addEventListener("pointercancel", this.onUp);
    el.addEventListener("wheel", this.onWheel, { passive: false });
  }

  onTap(fn: TapHandler): void {
    this.tapHandlers.push(fn);
  }
  onPinch(fn: PinchHandler): void {
    this.pinchHandlers.push(fn);
  }
  onDrag(fn: DragHandler): void {
    this.dragHandlers.push(fn);
  }
  onDragStart(fn: DragStartHandler): void {
    this.dragStartHandlers.push(fn);
  }
  onDragEnd(fn: DragEndHandler): void {
    this.dragEndHandlers.push(fn);
  }

  private emitTap(e: { clientX: number; clientY: number }): void {
    for (const h of this.tapHandlers) h(e);
  }
  private emitPinch(factor: number): void {
    for (const h of this.pinchHandlers) h({ factor });
  }
  private emitDrag(dx: number, dy: number, clientX: number, clientY: number): void {
    for (const h of this.dragHandlers) h({ dx, dy, clientX, clientY });
  }
  private emitDragStart(clientX: number, clientY: number): void {
    for (const h of this.dragStartHandlers) h({ clientX, clientY });
  }
  private emitDragEnd(): void {
    for (const h of this.dragEndHandlers) h();
  }

  private onDown = (e: PointerEvent): void => {
    this.el.setPointerCapture(e.pointerId);
    this.pointers.set(e.pointerId, {
      startX: e.clientX,
      startY: e.clientY,
      startT: performance.now(),
      x: e.clientX,
      y: e.clientY,
      moved: false,
    });
    if (this.pointers.size === 2) {
      this.pinchStartDist = this.currentPinchDist();
    }
  };

  private onMove = (e: PointerEvent): void => {
    const t = this.pointers.get(e.pointerId);
    if (!t) return;
    const dx = e.clientX - t.x;
    const dy = e.clientY - t.y;
    t.x = e.clientX;
    t.y = e.clientY;
    const wasMoved = t.moved;
    if (!t.moved) {
      const d = Math.hypot(t.x - t.startX, t.y - t.startY);
      if (d > TAP_MAX_MOVE) t.moved = true;
    }
    if (this.pointers.size === 1 && t.moved) {
      if (!wasMoved) {
        this.dragging = true;
        this.emitDragStart(t.startX, t.startY);
      }
      this.emitDrag(dx, dy, t.x, t.y);
    }
    if (this.pointers.size === 2 && this.pinchStartDist > 0) {
      const d = this.currentPinchDist();
      if (d > 0) {
        const factor = d / this.pinchStartDist;
        this.emitPinch(factor);
        this.pinchStartDist = d;
      }
    }
  };

  private onUp = (e: PointerEvent): void => {
    const t = this.pointers.get(e.pointerId);
    if (!t) return;
    const wasAlone = this.pointers.size === 1;
    const heldFor = performance.now() - t.startT;
    this.pointers.delete(e.pointerId);
    if (this.pointers.size < 2) this.pinchStartDist = 0;
    if (wasAlone && !t.moved && heldFor < TAP_MAX_TIME) {
      this.emitTap({ clientX: t.x, clientY: t.y });
    }
    if (wasAlone && this.dragging) {
      this.dragging = false;
      this.emitDragEnd();
    }
  };

  private onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    const factor = e.deltaY < 0 ? 1.1 : 1 / 1.1;
    this.emitPinch(factor);
  };

  private currentPinchDist(): number {
    const arr = [...this.pointers.values()];
    if (arr.length < 2) return 0;
    const dx = arr[0].x - arr[1].x;
    const dy = arr[0].y - arr[1].y;
    return Math.hypot(dx, dy);
  }
}
